import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Divider, Form, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';

import {
  ownerPhoneNotificationsSettingsUpdate,
  useOwnerPhoneNotificationSettings,
} from 'api/owner_sdk';
import { Button, Checkbox, IconButton, Link } from 'ui-kit';
import Wrapper from 'components/Wrapper';
import InputPhoneIntl from 'components/Form/InputPhoneIntl';
import NotificationChannels from 'components/Form/NotificationChannels';

import {
  formatPhoneNumber,
  getPhoneNumberCountry,
  isSmsSupportedCountry,
  isValidIntlPhoneNumber,
} from 'components/Form/InputPhoneMask/utils';
import './styles.scss';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { PaymentPlan } from 'api/types/owner';
import { flagsToNotificationChannel, notificationChannelToFlags } from 'utils/notificationChannel';

interface IPhoneNotificationSettingsForm {
  id: number | null;
  phone_number: string;
  is_sms_enabled: boolean;
  is_whatsapp_enabled: boolean;
  is_request_sms_enabled: boolean;
  is_sales_sms_enabled: boolean;
  is_marketing_sms_enabled: boolean;
}

const emptyPhoneNumber: IPhoneNotificationSettingsForm = {
  id: null,
  phone_number: '',
  is_sms_enabled: true,
  is_whatsapp_enabled: false,
  is_request_sms_enabled: false,
  is_sales_sms_enabled: false,
  is_marketing_sms_enabled: false,
};

const validSchema = yup.object({
  phone_notification_settings: yup.array().of(
    yup.object({
      phone_number: yup
        .string()
        .test(
          'valid-phone-number',
          'Please input a valid phone number.',
          (value) => _.isEmpty(value) || isValidIntlPhoneNumber(value),
        ),
    }),
  ),
});

export interface IAccountNotificationForm {
  ownerPlan: PaymentPlan;
}

const AccountNotificationForm = ({ ownerPlan }: IAccountNotificationForm) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const {
    data: phoneNotificationSettings,
    isLoading,
    mutate: updateSettings,
    error,
  } = useOwnerPhoneNotificationSettings();

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  const disabled = useMemo(() => ownerPlan === PaymentPlan.Basic, [ownerPlan]);

  const disabledAdditionalPhones = useMemo(
    () => _.includes([PaymentPlan.Basic, PaymentPlan.Pro], ownerPlan),
    [ownerPlan],
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: {
      phone_notification_settings: [emptyPhoneNumber],
    },
    resolver: yupResolver(validSchema),
  });

  const {
    fields,
    append: appendPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({
    control,
    name: 'phone_notification_settings',
  });

  // Keyed by useFieldArray's row id, which survives add/remove unlike the index.
  const [countries, setCountries] = useState<Record<string, string>>({});

  // A reset-time channel correction never marks the form dirty, so Save would stay disabled.
  const [hasCorrectedChannel, setHasCorrectedChannel] = useState(false);

  const handleCountryChange = useCallback(
    (rowId: string) => (country?: string) =>
      setCountries((previous) => ({ ...previous, [rowId]: country })),
    [],
  );

  useEffect(() => {
    if (phoneNotificationSettings) {
      if (_.isEmpty(phoneNotificationSettings)) {
        reset({
          phone_notification_settings: [emptyPhoneNumber],
        });
      } else {
        let corrected = false;

        reset({
          phone_notification_settings: phoneNotificationSettings.map((item) => {
            const smsSupported = isSmsSupportedCountry(getPhoneNumberCountry(item.phone_number));
            const { isSmsEnabled, isWhatsappEnabled } = notificationChannelToFlags(
              item.notification_channel,
            );

            corrected = corrected || (!smsSupported && isSmsEnabled);

            // Clamp here, not in NotificationChannels' effect: reset remounts the row, so a
            // post-reset flip updates form state but never repaints the checkbox.
            return {
              ...item,
              is_sms_enabled: smsSupported && isSmsEnabled,
              is_whatsapp_enabled: isWhatsappEnabled || (!smsSupported && isSmsEnabled),
            };
          }),
        });

        setHasCorrectedChannel(corrected);
      }
    }
  }, [phoneNotificationSettings]);

  const onSubmit = (data: {
    phone_notification_settings: Array<IPhoneNotificationSettingsForm>;
  }) => {
    const hasMissingData =
      data.phone_notification_settings.filter(
        (item) =>
          !_.isEmpty(item.phone_number) &&
          !item.is_sales_sms_enabled &&
          !item.is_request_sms_enabled &&
          !item.is_marketing_sms_enabled,
      ).length > 0;

    if (hasMissingData) {
      message.error('Select the text info you would like to receive.');
      return;
    }

    const hasMissingPhoneNumber =
      data.phone_notification_settings.filter(
        (item) =>
          _.isEmpty(item.phone_number) &&
          (item.is_request_sms_enabled ||
            item.is_sales_sms_enabled ||
            item.is_marketing_sms_enabled),
      ).length > 0;

    if (hasMissingPhoneNumber) {
      message.error('Phone number is required to receive text info.');
      return;
    }

    const hasMissingChannel =
      data.phone_notification_settings.filter(
        (item) =>
          !_.isEmpty(item.phone_number) && !item.is_sms_enabled && !item.is_whatsapp_enabled,
      ).length > 0;

    if (hasMissingChannel) {
      message.error('Select how you would like to receive notifications.');
      return;
    }

    showLoading();

    const formattedDataArray = data.phone_notification_settings.map((item) => {
      const phone = formatPhoneNumber({ phone: item.phone_number });

      const { is_sms_enabled, is_whatsapp_enabled, ...rest } = item;

      return {
        ...rest,
        phone_number: phone,
        notification_channel: flagsToNotificationChannel({
          isSmsEnabled: !_.isNil(phone) && is_sms_enabled,
          isWhatsappEnabled: !_.isNil(phone) && is_whatsapp_enabled,
        }),
      };
    });

    ownerPhoneNotificationsSettingsUpdate({ phoneNotificationSettings: formattedDataArray })
      .then(() => {
        message.success('Successfully saved account notification preferences.');
        updateSettings();

        hideLoading();
      })
      .catch((e) => {
        message.error(e.message);
        hideLoading();
      });
  };

  return (
    <>
      {!isLoading && (
        <Wrapper className="hc-settings notifications">
          <Form
            name="settings-notifications"
            layout="vertical"
            initialValues={phoneNotificationSettings}
            style={{ marginBottom: '16px' }}
          >
            {fields.map((field, index) => (
              <div key={field.id} className="phone-number-block">
                <div>
                  <div className="phone-number-row">
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.phone_number`}
                      render={({
                        field: { name, value, onChange, onBlur },
                        fieldState: { error },
                      }) => {
                        return (
                          <Form.Item
                            name={name}
                            label="Phone number"
                            className="phone-number-label"
                            validateStatus={error ? 'error' : ''}
                            help={error ? error?.message : null}
                          >
                            <input value={value} type="hidden" />
                            <InputPhoneIntl
                              onBlur={onBlur}
                              onChange={onChange}
                              value={value}
                              disabled={disabled}
                              onCountryChange={handleCountryChange(field.id)}
                            />
                          </Form.Item>
                        );
                      }}
                    />
                    {!disabled && (
                      <IconButton
                        className="phone-number-row_remove"
                        onClick={() => removePhoneNumber(index)}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    )}
                  </div>
                  <Controller
                    control={control}
                    name={`phone_notification_settings.${index}.is_sms_enabled`}
                    render={({ field: { value: smsEnabled, onChange: onSmsChange } }) => (
                      <Controller
                        control={control}
                        name={`phone_notification_settings.${index}.is_whatsapp_enabled`}
                        render={({
                          field: { value: whatsappEnabled, onChange: onWhatsappChange },
                        }) => (
                          <NotificationChannels
                            smsEnabled={smsEnabled}
                            whatsappEnabled={whatsappEnabled}
                            disabled={disabled}
                            smsSupported={isSmsSupportedCountry(
                              countries[field.id] ??
                                getPhoneNumberCountry(
                                  watch(`phone_notification_settings.${index}.phone_number`),
                                ),
                            )}
                            onChange={(next) => {
                              onSmsChange(next.is_sms_enabled);
                              onWhatsappChange(next.is_whatsapp_enabled);
                            }}
                          />
                        )}
                      />
                    )}
                  />
                  <div className="list_notifications">
                    <p className="label-title">Enable notifications for:</p>
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_request_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            Request and Approve: Request, Approve, Deny, Cancelled, Expired
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_sales_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            Sales: Purchase, Cancellation, Service Reminders, Inventory
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`phone_notification_settings.${index}.is_marketing_sms_enabled`}
                      render={({ field: { name, value, onChange } }) => (
                        <Form.Item name={name}>
                          <Checkbox checked={value} onChange={onChange} disabled={disabled}>
                            News and Feature Updates
                          </Checkbox>
                        </Form.Item>
                      )}
                    />
                    {disabled && (
                      <p className="upgrade-txt">
                        * <Link href={`/pricing/`}>Upgrade to Pro</Link> for text messaging
                      </p>
                    )}
                  </div>
                  <Divider />
                </div>
              </div>
            ))}
            <Button
              disabled={disabledAdditionalPhones}
              variant="outlined"
              onClick={() => appendPhoneNumber(emptyPhoneNumber)}
              startIcon={<PlusOutlined />}
            >
              Add more
            </Button>
            {disabledAdditionalPhones && (
              <p className="upgrade-txt">
                * <Link href={`/pricing/`}>Upgrade to Pro Plus</Link> for adding more than one phone
                number
              </p>
            )}
            <Divider />

            <Form.Item noStyle shouldUpdate>
              {() => (
                <Button
                  variant="contained"
                  type="submit"
                  onClick={(data) => handleSubmit(onSubmit)(data)}
                  disabled={(!isDirty && !hasCorrectedChannel) || isLoading}
                >
                  Save Preferences
                </Button>
              )}
            </Form.Item>
          </Form>
        </Wrapper>
      )}
    </>
  );
};

export default AccountNotificationForm;
